import React, { useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import CheckIcon from '../../../assets/icons/svg/large-icons/ic-large-mail-check.svg';
import Icon from '../../../generic-components/Icon';
import { useGetSupplierData } from '../../Supplier/hooks/useGetSupplierData';
import { useStore } from '../../../store';
import { ISupplier } from '../../Supplier/types';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { STYLES } from '../consts';
import { VIEWS_MAP } from '../../../router/constants';
import { SUPPLIER_REFERRER_TYPES } from '../../Supplier/consts';
import Spinner, { SpinnerColor } from '../../../generic-components/Spinner';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { DYNAMIC_FORM_TYPES } from '../../../components/form/dynamicForm/types';

export const Intro = () => {
  const { feeData, setActiveStep } = useStore(state => state);
  const [suppliers, setSuppliers] = useState<ISupplier[]>([]);
  const { refetch: getSupplierList, isRefetching: isRefetchSupplierList, isLoading: isLoadingSupplierList } = useGetSupplierData({ });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();

  const isLoading = useMemo(() => isRefetchSupplierList || isLoadingSupplierList, [isRefetchSupplierList, isLoadingSupplierList]);

  const handleSubmit = async () => {
    trackMixPanel('Act on Move Money Intro Screen', { Action: 'Let’s Start', Flow: 'Mail a Check' });
    if (suppliers.length) {
      navigate(
        `/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.PICKER}`,
        { state: { referrer: SUPPLIER_REFERRER_TYPES.MAIL_CHECK, closeUrl: `/${VIEWS_MAP.MAIL_A_CHECK}` } },
      );
    } else {
      navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.MANAGE.BASE}`, {
        state: {
          pageTitle: 'Mail a Check',
          formHeader: 'Who would you like to send the check to?',
          formType: DYNAMIC_FORM_TYPES.MAIL_A_CHECK,
          onExitDestination: `/${VIEWS_MAP.DASHBOARD}`,
        },
      });
    }
  };

  useLayoutEffect(() => {
    trackMixPanel('View Move Money Intro Screen', { Origin: 'Menu', Flow: 'Mail a Check' });
    getSupplierList()
      .then((res) => {
        const data: ISupplier[] = res.data.suppliers;
        if (state?.redirectStep) {
          setActiveStep(state.redirectStep);
        } else if (data.length) {
          navigate(
            `/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.PICKER}`,
            { state: { referrer: SUPPLIER_REFERRER_TYPES.MAIL_CHECK, closeUrl: `/${VIEWS_MAP.MAIL_A_CHECK}` } },
          );
        } else {
          setSuppliers(data);
        }
      });
  }, []);

  return isLoading ? <Spinner spinnerColor={SpinnerColor.Blue} width='50px' height='50px' /> : (
    <div className={STYLES.INTRO.WRAPPER}>
      <div className={STYLES.INTRO.HEADER_SECTION}>
        <Heading
          title={t('MAIL_CHECK.INTRO.TITLE')}
          className={STYLES.INTRO.TITLE}
        />
      </div>

      <div className={STYLES.INTRO.BODY_WRAPPER}>
        <Icon className='w-[100px]' src={CheckIcon} />
        <Heading title={t('MAIL_CHECK.INTRO.SUB_TITLE')} className={STYLES.INTRO.BODY_SUB_TITLE} />
        <Heading title={`${t('MAIL_CHECK.INTRO.FEE_PRE')} ${convertValueToUSD(feeData?.fee, 2)} ${t('MAIL_CHECK.INTRO.FEE_POST')}`} className={STYLES.INTRO.FEE_DISCLAIMER} />
        <Button onClick={handleSubmit} isLoading={isLoading} disable={isLoading} title={t('MAIL_CHECK.INTRO.CTA')} className={STYLES.INTRO.CTA} id='introSubmit' />
      </div>

      <div className={STYLES.INTRO.FOOTER_WRAPPER}>
        <Heading title={t('MAIL_CHECK.INTRO.DISCLAIMER')} className={STYLES.INTRO.FOOTER_CONTENT} />
      </div>
    </div>
  );
};
